import { useMutation, type InternalRefetchQueriesInclude } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import { useCallback } from "react";

export const GENERATE_PERMIT_RENEWALS_MUTATION = gql(`
  mutation GeneratePermitRenewals($input: GeneratePermitRenewalsInput!) {
    generatePermitRenewals(input: $input) {
      processedCount
      processedDocumentIds
      skippedCount
      skippedDocumentIds
    }
  }
`);

type UseGeneratePermitRenewalsProps = {
  onSuccess?: () => void;
  refetchQueries?: InternalRefetchQueriesInclude;
};

export function useGeneratePermitRenewals(props?: UseGeneratePermitRenewalsProps) {
  const alerts = useAlerts();
  const onSuccess = props?.onSuccess;
  const [mutate, result] = useMutation(GENERATE_PERMIT_RENEWALS_MUTATION, {
    refetchQueries: props?.refetchQueries,
  });

  const generatePermitRenewals = useCallback(
    async (documentIds: string[]) => {
      if (documentIds.length === 0) {
        return;
      }

      try {
        const response = await mutate({
          variables: { input: { documentIds } },
        });
        const data = response.data?.generatePermitRenewals;
        if (!data) {
          throw new Error("No result returned");
        }

        const { processedCount, skippedCount } = data;
        if (processedCount > 0 && skippedCount === 0) {
          alerts.success(
            processedCount === 1
              ? "Permit renewal task generated"
              : `${processedCount} permit renewal tasks generated`
          );
        } else if (processedCount > 0) {
          alerts.success(
            `${processedCount} permit renewal task${processedCount === 1 ? "" : "s"} generated; ${skippedCount} skipped`
          );
        } else {
          // UI only offers this for eligible permits; an all-skip is unexpected.
          alerts.error("Failed to generate permit renewal tasks");
        }

        onSuccess?.();
      } catch (error) {
        alerts.error("Failed to generate permit renewal tasks", error);
      }
    },
    [alerts, mutate, onSuccess]
  );

  return { generatePermitRenewals, result };
}
